<template>
  <div class="compatibility-quick-view">
    <div
      v-if="compatibilityTags?.length"
      class="compatibility-quick-view__icons"
    >
      <MzaIcon
        v-if="isMzaCompatible"
        size="large"
        direction="right"
        class="compatibility-quick-view__mza-icon"
      />
      <MzaIcon
        v-if="isMshCompatible"
        size="large"
        direction="right"
        class="compatibility-quick-view__mza-icon"
        version="msh"
      />
    </div>

    <!-- On bundles we can only show if a charger is included-->
    <Charger
      v-if="showCharger && offer.product?.bundle"
      class="compatibility-quick-view__charger"
      :charger-included="hasChargerIncluded"
    />
    <Charger
      v-else-if="showCharger && offer.product"
      class="compatibility-quick-view__charger"
      :common-charger="offer.product.commonCharger"
    />

    <KompalisteLink :eans="offer.product?.eans || []" />
  </div>
</template>

<script lang="ts" setup>
import { computed, type PropType } from 'vue';

import KompalisteLink from '@/components/kompaliste-link.vue';
import MzaIcon from '@/components/mza-icon.vue';
import { getBundleProduct, getStandardProduct, type Offer } from '~/lib/goliath/offer-by-slugs';
import { Charger } from '@i22/td-longtail-vue-components';
import type { BundleProduct } from '~/lib/goliath/goliath.types';

const props = defineProps({
  offer: { type: Object as PropType<Offer>, default: () => ({}) },
});

const getCommonTags = (bundledProducts: BundleProduct[]) => {
  const appTags = ['mzapro', 'mzabasic', 'mshpro', 'mshfree'];

  return appTags.filter((tag) =>
    bundledProducts.every((product) =>
      product.tags?.some((productTag) => productTag?.toLowerCase() === tag)
    )
  );
};

const compatibilityTags = computed(() => {
  const { bundledProducts } = props.offer.product;

  switch (Boolean(bundledProducts)) {
    case true:
      if (bundledProducts.length < 1) return [];
      return getCommonTags(bundledProducts);
    default:
      return props.offer.product?.tags || [];
  }
});

const isMzaCompatible = computed(() => {
  if (compatibilityTags.value?.length < 1) return false;
  const lowerCaseTags = compatibilityTags.value.map((tag) => tag?.toLowerCase());
  return lowerCaseTags.some((tag) => ['mzabasic', 'mzapro'].includes(tag));
});

const isMshCompatible = computed(() => {
  if (compatibilityTags.value?.length < 1) return false;
  const lowerCaseTags = compatibilityTags.value.map((tag) => tag?.toLowerCase());
  return lowerCaseTags.some((tag) => ['mshfree', 'mshpro'].includes(tag));
});

const showCharger = computed(() => {
  if (!props.offer.product) return false;
  const standardProduct = getStandardProduct(props.offer.product);
  if (standardProduct?.commonCharger?.possible) return true;
  const bundleProduct = getBundleProduct(props.offer.product);
  return bundleProduct?.bundledProducts?.find((prod) => prod?.commonCharger?.possible);
});

const hasChargerIncluded = computed(() => {
  if (!props.offer.product) return false;
  const standardProduct = getStandardProduct(props.offer.product);
  if (standardProduct?.commonCharger?.included) return true;
  const bundleProduct = getBundleProduct(props.offer.product);
  return bundleProduct?.bundledProducts?.find((prod) => prod?.commonCharger?.included === true);
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.compatibility-quick-view {
  &__icons,
  &__compatible-apps-wrapper {
    display: flex;
    gap: $grid-base;
  }

  &__app-icon {
    width: 4 * $grid-base;
    height: 4 * $grid-base;
  }

  &__charger {
    font-size: 5em;
  }

  &__mza-icon {
    &:first-child {
      z-index: 2;
    }
  }
}
</style>
